import axios from "axios";
import { notifySegmentsChanged } from '../../utils/segmentsChanged.js';
import { config } from '../../config/Config';
import { cancelChatStream, streamChat } from '../chat/ChatStreamService.js';

// Owning-Geography (version-chain) id is the stable id the backend keys segment scope on.
const geographyId = (dto) => dto?.versionInfo?.parentEntityId || dto?.id;

// Invite payload shape: game-level geography map { [geoSegmentId]: { role } }. Empty (no ids) = whole game.
export const toSegmentDetailsMap = (geoIds = [], role) =>
    (geoIds || []).reduce((map, id) => { map[id] = { role }; return map; }, {});

export const getActiveGeoSegments = async (gameId) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/player-segment/active`,
            // Override the active-game header for this one call to list a specific game's segments.
            headers: gameId ? { 'X-Web-Game-Id': gameId } : undefined,
        });
        const segments = response.data?.segments || [];
        return segments
            .filter(s => s.segmentType === 'GEO')
            // parentId: a sub-geography's parent, by the same version-chain id `id` uses.
            .map(s => ({ id: geographyId(s), name: s.segmentName, countries: s.countries || [], parentId: s.parentSegmentId || null }));
    } catch (error) {
        console.error('Error fetching active geo segments:', error.response?.data || error.message);
        return [];
    }
};

/**
 * The geographies the caller may view in a game, and whether their access covers the whole game - for the
 * header's geography picker. Not page-gated (a geography-limited user has no access until one is chosen).
 * Returns { wholeGame, geographies: [{ id, name, countries, parentId }] }; a failure reads as "nothing",
 * unless `keepOnError` - a background re-read, where a passing failure must not wipe the switcher - in which
 * case it resolves to null and the caller keeps what it has.
 */
export const getMyGeographies = async (gameId, { keepOnError = false } = {}) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/player-segment/my-geographies`,
            headers: gameId ? { 'X-Web-Game-Id': gameId } : undefined,
        });
        return {
            wholeGame: !!response.data?.wholeGame,
            geographies: (response.data?.segments || [])
                .filter(s => s.segmentType === 'GEO')
                .map(s => ({ id: geographyId(s), name: s.segmentName, countries: s.countries || [], parentId: s.parentSegmentId || null })),
        };
    } catch (error) {
        console.error('Error fetching my geographies:', error.response?.data || error.message);
        return keepOnError ? null : { wholeGame: false, geographies: [] };
    }
};

// Segment scope comes from the X-Web-Game-Id header; pass gameId so a page addressed by URL slug
// reads and writes the same game regardless of what the header context currently holds.
const scoped = (gameId, extra = {}) => (gameId ? { 'X-Web-Game-Id': gameId, ...extra } : extra);

// The tenant list carries a synthetic ALL_PLAYERS row meaning "everyone". That is the Global
// bucket the UI already draws, so surfacing it as well would list the same audience twice — once
// as the container and once as a segment inside it.
const ALL_PLAYERS_ID = 'ALL_PLAYERS';

export const getPlayerSegments = async (gameId) => {
    const response = await axios({
        method: 'get',
        url: `${config.serverBaseUrl}/v1/web/player-segment`,
        headers: scoped(gameId),
    });
    return (response.data?.segments || []).filter(s => s?.id !== ALL_PLAYERS_ID);
};

// Only the segments something can target: live ones and new ones not used yet. Drafts and archived ones are
// left out, so a picker offers nothing the server would refuse.
export const getActivePlayerSegments = async (gameId) => {
    const response = await axios({
        method: 'get',
        url: `${config.serverBaseUrl}/v1/web/player-segment/active`,
        headers: scoped(gameId),
    });
    return (response.data?.segments || []).filter(s => s?.id !== ALL_PLAYERS_ID);
};

export const createPlayerSegment = async (data, gameId) => {
    const response = await axios({
        method: 'post',
        url: `${config.serverBaseUrl}/v1/web/player-segment`,
        headers: scoped(gameId, { 'Content-Type': 'application/json' }),
        data,
    });
    notifySegmentsChanged(gameId);
    return response.data?.segment;
};

/**
 * Amends a segment, creating a new version.
 *
 * Takes the version-CHAIN id, not a version's own `_id`: the endpoint resolves the row to amend
 * with `findLatestByParentEntityId`. The two are the same string only for version 1 — every later
 * version is a new document with its own `_id` — so passing `dto.id` worked on a segment's first
 * edit and then 404'd ("The requested resource was not found") on every edit after it. Use
 * `chainId(dto)` / `dto.versionInfo.parentEntityId`.
 */
export const updatePlayerSegment = async (chainId, data, gameId) => {
    const response = await axios({
        method: 'put',
        url: `${config.serverBaseUrl}/v1/web/player-segment/${chainId}`,
        headers: scoped(gameId, { 'Content-Type': 'application/json' }),
        data,
    });
    notifySegmentsChanged(gameId);
    return response.data?.segment;
};

/**
 * Deletes a segment completely — every version — and everything scoped under it, in one request.
 *
 * The server checks the whole tree first and refuses (409, with the reason) while anything live
 * uses the segment or anything inside it, then deletes deepest first. This used to be walked here,
 * one version per request, which could stop half way and leave a geography half deleted.
 */
export const deleteSegmentTree = async (chainId, gameId) => {
    await axios({
        method: 'delete',
        url: `${config.serverBaseUrl}/v1/web/player-segment/${chainId}/tree`,
        headers: scoped(gameId),
    });
    notifySegmentsChanged(gameId);
    return true;
};

export const clonePlayerSegment = async (segmentId, gameId) => {
    const response = await axios({
        method: 'post',
        url: `${config.serverBaseUrl}/v1/web/player-segment/${segmentId}/clone`,
        headers: scoped(gameId),
    });
    notifySegmentsChanged(gameId);
    return response.data?.segment;
};

// What uses a segment, each with `live` as the server judges it: [{ type, id, relation, live }].
export const getSegmentDependents = async (segmentChainId, gameId) => {
    const response = await axios({
        method: 'get',
        url: `${config.serverBaseUrl}/v1/web/player-segment/${segmentChainId}/usage`,
        headers: scoped(gameId),
    });
    // A 200 with some other body (SPA fallback, proxy error) must not reach a caller's forEach.
    return Array.isArray(response.data?.dependents) ? response.data.dependents : [];
};

/* ── AI drafting ───────────────────────────────────────────────────────────────────────────────
   Both entry points below run on the same streaming chat gateway; intel routes the turn to its
   player-segments agent off the page key. There is no REST "propose" endpoint — drafting a segment
   is an agent turn, and the agent needs the conversation to refine one.

   The page key is sent instead of the live URL: intel resolves any "segment"-containing value to
   the player_segments agent, and a fixed key survives a route rename that a real path would not. */
const SEGMENT_PAGE_KEY = 'player-segments';
/** Intel's agent key for this chat (metadata.page). */
const SEGMENT_AGENT = 'player_segments';

/* The agent writes markdown. Nothing on the segment pages renders it, so strip the emphasis and
   code marks that would otherwise show as literal asterisks and backticks in a banner. */
const plainText = (text) => (text ? String(text).replace(/\*\*|`/g, '').trim() : '');

/**
 * Draft turn: run one turn and return the rule tree it proposes, plus the {@code convId} to pass
 * back so the next turn continues the same conversation.
 *
 * Keeps the {@code {ok, tree}} shape so a caller reads the outcome without knowing a stream was
 * involved. {@code tree} is whatever intel put in {@code segmentExpressionTree} — a JSON-encoded
 * string on the wire, which {@code parseTree} accepts as-is.
 */
export const proposeSegmentRule = async (sentence, gameId, { context = '', convId = null } = {}) => {
    let tree = null;
    let failure = null;
    let reply = null;
    let conversation = convId;
    try {
        await streamChat(
            { message: sentence, convId, page: SEGMENT_AGENT, currentPage: SEGMENT_PAGE_KEY, context, gameId },
            (event, data) => {
                if (event === 'chat_started') {
                    conversation = data?.conv_id || conversation;
                } else if (event === 'segment_proposal' && data?.segment) {
                    // Last proposal wins: a turn that revises itself should land on its final answer.
                    tree = data.segment.segmentExpressionTree ?? null;
                } else if (event === 'completed') {
                    reply = data?.response || reply;
                } else if (event === 'error') {
                    failure = data?.error || 'The assistant could not draft that rule.';
                }
            },
        );
    } catch (e) {
        return { ok: false, error: e?.message || 'Could not reach the assistant.', convId: conversation };
    }
    if (failure) return { ok: false, error: failure, convId: conversation };
    /* The agent answered but proposed no rule. Its own reply says why — a missing attribute, a
       question that is not about segments — and that reason is the whole answer, so pass it on
       rather than replacing it with a generic line. */
    if (!tree) {
        return { ok: false, error: plainText(reply) || 'The assistant did not propose a rule from that.', convId: conversation };
    }
    return { ok: true, tree, convId: conversation };
};

/**
 * Conversational draft: stream one turn of the segment assistant, for the builder's AI panel.
 *
 * Thin wrapper over the shared gateway that pins the page key, so the builder never has to know
 * which agent answers. Pass {@code convId} to continue the same conversation, and
 * {@code opts.signal} to stop consuming mid-stream.
 */
export const streamSegmentChat = async ({ message, convId, context, gameId }, onEvent, opts = {}) =>
    streamChat(
        { message, convId, page: SEGMENT_AGENT, currentPage: SEGMENT_PAGE_KEY, context, gameId },
        onEvent,
        opts,
    );

/** Stop an in-flight draft server-side (request_id from the chat_started frame). */
export const cancelSegmentChat = cancelChatStream;

// Real audience size from ClickHouse, for a rule still being authored. ClickHouse is an optional
// backend dependency, so a null here means "no count available", not "no players" — callers show
// the placeholder rather than a zero that would read as a real answer.
// `parentSegmentId` is the Geography the draft sits in, so "Whales" inside "India" is counted inside India.
export const countMatchingPlayersByQuery = async (query, gameId, parentSegmentId = null) => {
    if (!query) return null;
    try {
        const response = await axios({
            method: 'post',
            url: `${config.serverBaseUrl}/v1/web/player-segment/matching-count`,
            headers: scoped(gameId, { 'Content-Type': 'application/json' }),
            data: parentSegmentId ? { query, parentSegmentId } : { query },
        });
        return response.data?.count ?? null;
    } catch {
        return null;
    }
};

// Same, for a saved segment — the backend falls back to its stored clickHouseQueryExpression.
export const countMatchingPlayersForSegment = async (segmentId, gameId) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${config.serverBaseUrl}/v1/web/player-segment/${segmentId}/matching-count`,
            headers: scoped(gameId, { 'Content-Type': 'application/json' }),
            data: {},
        });
        return response.data?.count ?? null;
    } catch {
        return null;
    }
};
